// McpPrefsModal.tsx — the composer's "MCP tools" modal. Lists every
// MCPServer the manager knows about; the user toggles which ones power
// tool-calling inside their sessions. The tool execution loop is
// server-side — this only edits the stored preference (useMcpPrefs.ts).
import React from 'react';
import { Badge, Button, Form, Modal, Spinner, Stack } from 'react-bootstrap';
import type { McpPrefs } from './useMcpPrefs';

const McpPrefsModal: React.FC<{ prefs: McpPrefs }> = ({ prefs }) => (
  <Modal show={prefs.modalOpen} onHide={prefs.closeModal} data-bs-theme="dark">
    <Modal.Header closeButton>
      <Modal.Title>MCP tools</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {prefs.servers.length === 0 ? (
        <div className="text-muted small">
          No MCP servers are registered on this cluster yet. Add one from the MCP Servers admin
          page; it'll appear here for you to enable.
        </div>
      ) : (
        <>
          <p className="text-muted small mb-3">
            Enable MCP servers whose tools your chat sessions should be able to call.
            Changes apply to your next message; in-flight replies finish without them.
          </p>
          <Stack gap={2}>
            {prefs.servers.map((srv) => (
              <Form.Check
                key={srv.id}
                type="switch"
                id={`mcp-pref-${srv.id}`}
                // Built-ins are always-on for anyone the manager lists them
                // to: forced checked, not toggleable.
                checked={srv.builtin ? true : prefs.draft.has(srv.id)}
                disabled={srv.builtin}
                onChange={() => !srv.builtin && prefs.toggleDraft(srv.id)}
                label={
                  <span>
                    <span className="fw-semibold">{srv.name || srv.id}</span>
                    {srv.builtin && (
                      <Badge bg="info" className="ms-2" pill>built-in</Badge>
                    )}
                    {srv.command && (
                      <small className="text-muted ms-2">
                        {srv.command}{srv.args && srv.args.length > 0 ? ` ${srv.args.join(' ')}` : ''}
                      </small>
                    )}
                  </span>
                }
              />
            ))}
          </Stack>
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-light" size="sm" onClick={prefs.closeModal} disabled={prefs.saving}>
        Cancel
      </Button>
      <Button variant="primary" size="sm" onClick={prefs.save} disabled={prefs.saving}>
        {prefs.saving ? <Spinner animation="border" size="sm" /> : 'Save'}
      </Button>
    </Modal.Footer>
  </Modal>
);

export default McpPrefsModal;
